import { Component, OnInit, Input, SimpleChanges, OnChanges, OnDestroy } from '@angular/core';
import { observable, computed } from 'mobx-angular';
import { IVehicleAddress } from '../../../../interfaces/task-info';
import { JobScreenTaskTimesTask } from '../../../../interfaces/screen-specific/job-screen';
import { TaskSupplierScheduleStore } from '../../../task-supplier-schedule/task-supplier-schedule-store';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';
import { EventService, JobsService, TaskService } from '../../../../services';
import { Subscription } from 'rxjs';

enum DateTimeColumnType {
  PickUpEarliest = 0,
  PickUpLatest = 1,
  DropOffEarliest = 2,
  DropOffLatest = 3,
}

interface DateTimeColumn {
  title: string;
  date: Date;
  type: DateTimeColumnType;
  iconClass: string;
}

@Component({
  templateUrl: './job-screen-task-times.component.html',
  selector: 'job-screen-task-times',
  styleUrls: ['./job-screen-task-times.component.scss'],
})
export class JobScreenTaskTimesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() @observable public taskId: number;
  @Input() @observable public orgTypeId: number;

  public task: JobScreenTaskTimesTask;
  fetchingTaskData = true;
  private jobScreenEventSub: Subscription;

  @computed
  public get pickupLocation(): IVehicleAddress {
    return this.task?.pickupLocation;
  }

  @computed
  public get dropoffLocation(): IVehicleAddress {
    return this.task?.dropoffLocation;
  }

  @computed
  public get pickupEarliestDateTime(): Date {
    return this.task?.pickupEarliestDateTime;
  }

  @computed
  public get pickupLatestDateTime(): Date {
    return this.task?.pickupLatestDateTime;
  }

  @computed
  public get dropoffEarliestDateTime(): Date {
    return this.task?.dropoffEarliestDateTime;
  }

  @computed
  public get dropoffLatestDateTime(): Date {
    return this.task?.dropoffLatestDateTime;
  }

  // Original task dates/times for rescheduled task
  @computed
  public get originalPickupEarliestDateTime(): Date {
    return this.task?.originalPickupEarliestDateTime;
  }

  @computed
  public get originalPickupLatestDateTime(): Date {
    return this.task?.originalPickupLatestDateTime;
  }

  @computed
  public get originalDropoffEarliestDateTime(): Date {
    return this.task?.originalDropoffEarliestDateTime;
  }

  @computed
  public get originalDropoffLatestDateTime(): Date {
    return this.task?.originalDropoffLatestDateTime;
  }

  @Input() public height: number = 300;

  public dateTimeColumnType = DateTimeColumnType;
  private originalDateTimeColumns: { [key: number]: DateTimeColumn };

  constructor(
    private taskService: TaskService,
    private jobService: JobsService,
    private eventService: EventService,
  ) {
    this.handleJobScreenEvents();
  }

  ngOnDestroy(): void {
    this.jobScreenEventSub?.unsubscribe();
  }

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.taskId && this.taskId != null) {
      void this.getTaskTimes();
    }
  }

  private handleJobScreenEvents() {
    this.jobScreenEventSub = this.eventService.JobScreenEvent.subscribe((event) => {
      if (event.event == 'refresh-task-times') {
        void this.getTaskTimes();
      }
    });
  }

  async getTaskTimes() {
    try {
      this.task = null;
      this.dateTimeColumns = null;
      this.fetchingTaskData = true;
      this.task = await this.taskService.getForComponent<JobScreenTaskTimesTask>(
        this.taskId,
        this.orgTypeId == OrganisationTypeEnum.Supplier
          ? 'job-screen-task-times-adaptive'
          : 'job-screen-task-times-customer-windows',
      );
      void this.setup();
    } catch (error) {
    } finally {
      this.fetchingTaskData = false;
    }
  }

  dateVal(ct: DateTimeColumnType) {
    if (this.dateTimeColumns == null || ct == null) {
      return null;
    }
    return this.dateTimeColumns[ct]?.date;
  }

  dateValOriginal(ct: DateTimeColumnType) {
    if (this.originalDateTimeColumns == null || ct == null) {
      return null;
    }
    return this.originalDateTimeColumns[ct]?.date;
  }

  private dateTimeColumns: { [key: number]: DateTimeColumn };

  async setup() {
    const isCustomer = this.orgTypeId == OrganisationTypeEnum.Customer;

    this.dateTimeColumns = {
      0: {
        title: 'Earliest Collection',
        date: isCustomer ? this.originalPickupEarliestDateTime : this.pickupEarliestDateTime,
        type: DateTimeColumnType.PickUpEarliest,
        iconClass: 'text-pick-up-colour',
      },
      1: {
        title: 'Latest Collection',
        date: isCustomer ? this.originalPickupLatestDateTime : this.pickupLatestDateTime,
        type: DateTimeColumnType.PickUpLatest,
        iconClass: 'text-pick-up-colour',
      },
      2: {
        title: 'Earliest Delivery',
        date: isCustomer ? this.originalDropoffEarliestDateTime : this.dropoffEarliestDateTime,
        type: DateTimeColumnType.DropOffEarliest,
        iconClass: 'text-drop-off-colour',
      },
      3: {
        title: 'Latest Delivery',
        date: isCustomer ? this.originalDropoffLatestDateTime : this.dropoffLatestDateTime,
        type: DateTimeColumnType.DropOffLatest,
        iconClass: 'text-drop-off-colour',
      },
    };

    if (this.task.isRescheduled && this.orgTypeId == OrganisationTypeEnum.Engineius) {
      this.originalDateTimeColumns = {
        0: {
          title: 'Original Earliest Collection',
          date: this.originalPickupEarliestDateTime,
          type: DateTimeColumnType.PickUpEarliest,
          iconClass: 'text-pick-up-colour',
        },
        1: {
          title: 'Original Latest Collection',
          date: this.originalPickupLatestDateTime,
          type: DateTimeColumnType.PickUpLatest,
          iconClass: 'text-pick-up-colour',
        },
        2: {
          title: 'Original Earliest Delivery',
          date: this.originalDropoffEarliestDateTime,
          type: DateTimeColumnType.DropOffEarliest,
          iconClass: 'text-drop-off-colour',
        },
        3: {
          title: 'Original Latest Delivery',
          date: this.originalDropoffLatestDateTime,
          type: DateTimeColumnType.DropOffLatest,
          iconClass: 'text-drop-off-colour',
        },
      };
    }
  }
}
